import { useEffect, useState, type FormEvent } from "react";
import { CalendarDays, FlaskConical, TestTubeDiagonal, X } from "lucide-react";
import {
  pocCycleInputSchema,
  trialStateInputSchema,
  type PocTenantView,
  type TrialTenantView,
} from "@ubisafe/shared";
import { api, ApiClientError } from "../lib/api";

type UsageTenant = PocTenantView | TrialTenantView;

interface TenantUsageFormModalProps {
  tenant: UsageTenant;
  onClose: () => void;
  onSaved: (tenant: UsageTenant) => void;
}

type FieldName = "lead" | "startDate" | "expectedEndDate" | "commercialOwner" | "notes";
type FieldErrors = Partial<Record<FieldName, string[]>>;

function firstMessage(errors: FieldErrors, field: FieldName): string | undefined {
  return errors[field]?.[0];
}

export function TenantUsageFormModal({ tenant, onClose, onSaved }: TenantUsageFormModalProps) {
  const isPoc = tenant.classification === "POC";
  const current = isPoc ? tenant.activeCycle : tenant.state;
  const editingPoc = isPoc && Boolean(tenant.activeCycle);
  const [lead, setLead] = useState(current?.lead ?? "");
  const [startDate, setStartDate] = useState(current?.startDate ?? "");
  const [expectedEndDate, setExpectedEndDate] = useState(current?.expectedEndDate ?? "");
  const [commercialOwner, setCommercialOwner] = useState(current?.commercialOwner ?? "");
  const [notes, setNotes] = useState(current?.notes ?? "");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose, saving]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setFieldErrors({});

    let save: () => Promise<UsageTenant>;
    if (isPoc) {
      const parsed = pocCycleInputSchema.safeParse({ lead, startDate, expectedEndDate, commercialOwner, notes });
      if (!parsed.success) {
        setFieldErrors(parsed.error.flatten().fieldErrors as FieldErrors);
        return;
      }
      save = () =>
        editingPoc
          ? api.editActivePocCycle(tenant.identifier, parsed.data)
          : api.startPocCycle(tenant.identifier, parsed.data);
    } else {
      const parsed = trialStateInputSchema.safeParse({
        lead: lead || null,
        startDate: startDate || null,
        expectedEndDate: expectedEndDate || null,
        commercialOwner: commercialOwner || null,
        notes: notes || null,
      });
      if (!parsed.success) {
        setFieldErrors(parsed.error.flatten().fieldErrors as FieldErrors);
        return;
      }
      save = () => api.updateTrialState(tenant.identifier, parsed.data);
    }

    setSaving(true);
    try {
      onSaved(await save());
    } catch (requestError) {
      if (requestError instanceof ApiClientError) {
        setError(requestError.message);
        if (requestError.fieldErrors) setFieldErrors(requestError.fieldErrors as FieldErrors);
      } else {
        setError("Não foi possível salvar as informações deste ambiente.");
      }
    } finally {
      setSaving(false);
    }
  };

  const title = isPoc ? (editingPoc ? "Editar uso da POC" : "Iniciar uso da POC") : "Editar informações do Trial";
  const Icon = isPoc ? FlaskConical : TestTubeDiagonal;

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}
    >
      <section className="modal-card usage-modal" role="dialog" aria-modal="true" aria-labelledby="usage-modal-title">
        <header className="modal-card__header">
          <div className="modal-card__title">
            <span className={`modal-card__icon usage-modal__icon usage-modal__icon--${isPoc ? "poc" : "trial"}`}>
              <Icon aria-hidden="true" />
            </span>
            <div>
              <span className="eyebrow eyebrow--dark">{tenant.name}</span>
              <h2 id="usage-modal-title">{title}</h2>
              <small>Identificador {tenant.identifier}</small>
            </div>
          </div>
          <button className="icon-button" type="button" aria-label="Fechar" disabled={saving} onClick={onClose}>
            <X />
          </button>
        </header>

        <form className="modal-form usage-form" onSubmit={(event) => void submit(event)} noValidate>
          <div className="usage-form__grid">
            <div className="usage-form__wide">
              <label htmlFor="usage-lead">Lead{isPoc ? " *" : ""}</label>
              <input
                id="usage-lead"
                value={lead}
                onChange={(event) => setLead(event.target.value)}
                maxLength={160}
                autoFocus
                aria-invalid={Boolean(firstMessage(fieldErrors, "lead"))}
                aria-describedby={firstMessage(fieldErrors, "lead") ? "usage-lead-error" : undefined}
                placeholder="Nome da empresa de segurança"
              />
              {firstMessage(fieldErrors, "lead") && <small className="field-error" id="usage-lead-error">{firstMessage(fieldErrors, "lead")}</small>}
            </div>

            <div>
              <label htmlFor="usage-start-date">Data de início{isPoc ? " *" : ""}</label>
              <div className="date-field">
                <CalendarDays aria-hidden="true" />
                <input
                  id="usage-start-date"
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  aria-invalid={Boolean(firstMessage(fieldErrors, "startDate"))}
                  aria-describedby={firstMessage(fieldErrors, "startDate") ? "usage-start-date-error" : undefined}
                />
              </div>
              {firstMessage(fieldErrors, "startDate") && <small className="field-error" id="usage-start-date-error">{firstMessage(fieldErrors, "startDate")}</small>}
            </div>

            <div>
              <label htmlFor="usage-end-date">Previsão de término{isPoc ? " *" : ""}</label>
              <div className="date-field">
                <CalendarDays aria-hidden="true" />
                <input
                  id="usage-end-date"
                  type="date"
                  value={expectedEndDate}
                  min={startDate || undefined}
                  onChange={(event) => setExpectedEndDate(event.target.value)}
                  aria-invalid={Boolean(firstMessage(fieldErrors, "expectedEndDate"))}
                  aria-describedby={firstMessage(fieldErrors, "expectedEndDate") ? "usage-end-date-error" : undefined}
                />
              </div>
              {firstMessage(fieldErrors, "expectedEndDate") && <small className="field-error" id="usage-end-date-error">{firstMessage(fieldErrors, "expectedEndDate")}</small>}
            </div>

            <div className="usage-form__wide">
              <label htmlFor="usage-commercial">Comercial responsável{isPoc ? " *" : ""}</label>
              <input
                id="usage-commercial"
                value={commercialOwner}
                onChange={(event) => setCommercialOwner(event.target.value)}
                maxLength={120}
                aria-invalid={Boolean(firstMessage(fieldErrors, "commercialOwner"))}
                aria-describedby={firstMessage(fieldErrors, "commercialOwner") ? "usage-commercial-error" : undefined}
                placeholder="Nome do comercial responsável"
              />
              {firstMessage(fieldErrors, "commercialOwner") && <small className="field-error" id="usage-commercial-error">{firstMessage(fieldErrors, "commercialOwner")}</small>}
            </div>

            <div className="usage-form__wide">
              <label htmlFor="usage-notes">Anotações</label>
              <textarea
                id="usage-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                maxLength={4_000}
                rows={5}
                aria-invalid={Boolean(firstMessage(fieldErrors, "notes"))}
                aria-describedby={firstMessage(fieldErrors, "notes") ? "usage-notes-error" : "usage-notes-count"}
                placeholder="Contexto, próximos passos e observações da equipe"
              />
              <small className={firstMessage(fieldErrors, "notes") ? "field-error" : "field-counter"} id={firstMessage(fieldErrors, "notes") ? "usage-notes-error" : "usage-notes-count"}>
                {firstMessage(fieldErrors, "notes") ?? `${notes.length.toLocaleString("pt-BR")} de 4.000 caracteres`}
              </small>
            </div>
          </div>

          {!isPoc && (
            <div className="form-note usage-form__note usage-form__note--trial">
              O Trial é um ambiente livre: qualquer campo pode ser alterado ou apagado, sem ciclo, estado ou histórico.
            </div>
          )}

          {error && <div className="form-alert form-alert--error" role="alert">{error}</div>}

          <footer className="modal-actions">
            <button className="secondary-button" type="button" disabled={saving} onClick={onClose}>Cancelar</button>
            <button className="primary-button" type="submit" disabled={saving}>
              {saving ? "Salvando…" : isPoc ? (editingPoc ? "Salvar alterações" : "Iniciar POC") : "Salvar informações"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
